"use client";

import { useMemo, useState } from "react";
import { ComposedChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";
import { INDEX_PROXIES } from "@/lib/config";
import { computeNiceYAxisTicks, computeYearTicks, formatPriceAxisTick, withTimeField } from "@/lib/price-chart-utils";
import { formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ScoreBacktestWindowResult } from "@/lib/backtest/store";

/**
 * Score-Backtest-Chart (backlog.md -> Punkt 16) - gleiche Chart-Optik/Indexierungs-
 * Mechanik wie DepotVsIndexCard (Punkt 10): linear interpoliert, auf 100 indexiert,
 * zeitbasierte X-Achse. Fenster-Umschalter (5J/10J) statt Benchmark-Umschalter, da der
 * Vergleichsindex hier immer der S&P 500 ist (Universum = S&P 100, siehe
 * netlify/functions/score-backtest-capture.mts).
 */
export function ScoreBacktestChart({ windows }: { windows: ScoreBacktestWindowResult[] }) {
  const [windowYears, setWindowYears] = useState<5 | 10>(windows[0]?.windowYears ?? 5);
  const active = windows.find((w) => w.windowYears === windowYears) ?? windows[0];

  const chartData = useMemo(() => {
    if (!active) return [];
    return withTimeField(active.series).map((p) => ({
      time: p.time,
      portfolio: p.portfolioIndexed,
      benchmark: p.benchmarkIndexed,
    }));
  }, [active]);
  const axisTicks = useMemo(() => (active ? computeYearTicks(active.series, 8).map((d) => Date.parse(d)) : []), [active]);
  // Ersetzt Rechartsʼ Auto-Tick-Heuristik, die den obersten Tick teils nicht auf den
  // gleichen Rhythmus wie die uebrigen Ticks rundete (backlog.md -> Runde 5 Punkt 19).
  const yAxisTicks = useMemo(
    () =>
      computeNiceYAxisTicks(
        chartData.flatMap((d) => [d.portfolio, d.benchmark]).filter((v): v is number => v !== undefined),
        false,
      ),
    [chartData],
  );
  const last = active?.series[active.series.length - 1];

  if (!active) return null;

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-hairline p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg font-semibold text-ink">Score-Backtest: Top 20 vs. S&amp;P 500</h3>
          <p className="text-xs text-ink-secondary">
            Simuliertes, gleichgewichtetes Depot aus den {active.candidates.length} Aktien mit dem
            höchsten Erikonomie-Score im S&amp;P 100 zum Startzeitpunkt ({active.startYear}), seither
            unverändert gehalten - rein deskriptiv, keine Anlageempfehlung.
          </p>
        </div>
        <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
          {windows.map((w) => (
            <button
              key={w.windowYears}
              type="button"
              onClick={() => setWindowYears(w.windowYears)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                windowYears === w.windowYears ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
              )}
            >
              {w.windowYears} Jahre
            </button>
          ))}
        </div>
      </div>

      {active.series.length < 2 ? (
        <p className="text-xs text-muted-foreground">
          Für dieses Zeitfenster liegen noch nicht genug Kandidaten mit gültigem historischem Score vor.
        </p>
      ) : (
        <>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  scale="time"
                  ticks={axisTicks}
                  interval={0}
                  tickFormatter={(v: number) => formatPriceAxisTick(v, "Max")}
                  tick={{ fontSize: 12, fill: "#6B7280" }}
                  axisLine={{ stroke: "#ECEAE3" }}
                  tickLine={false}
                />
                <YAxis
                  domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
                  ticks={yAxisTicks}
                  tick={{ fontSize: 12, fill: "#6B7280" }}
                  tickFormatter={(v: number) => v.toFixed(0)}
                  width={48}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value, name) => [Number(value).toFixed(1), name]}
                  labelFormatter={(label) => new Date(Number(label)).toLocaleDateString("de-DE")}
                  contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13 }}
                />
                <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} />
                <Line type="linear" dataKey="portfolio" name="Score-Top-20-Depot" stroke="#16265C" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                <Line type="linear" dataKey="benchmark" name={INDEX_PROXIES.sp500.label} stroke="#C6A15B" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {last && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span className="text-ink-secondary">
                Score-Top-20-Depot:{" "}
                <span className={cn("font-tabular font-medium", last.portfolioIndexed >= 100 ? "text-positive" : "text-negative")}>
                  {formatPercent(last.portfolioIndexed / 100 - 1)}
                </span>
              </span>
              <span className="text-ink-secondary">
                {INDEX_PROXIES.sp500.label}:{" "}
                <span className={cn("font-tabular font-medium", last.benchmarkIndexed >= 100 ? "text-positive" : "text-negative")}>
                  {formatPercent(last.benchmarkIndexed / 100 - 1)}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">
                seit {new Date(active.series[0].date).toLocaleDateString("de-DE")}
              </span>
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap gap-1.5">
        {active.candidates.map((c) => (
          <span key={c.symbol} className="rounded-full border border-hairline bg-card px-2.5 py-1 text-xs text-ink-secondary">
            {c.symbol} <span className="text-muted-foreground">({Math.round(c.startScore)})</span>
          </span>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Universum: S&amp;P 100 (heutige Zusammensetzung, nicht die damalige - seither
        ausgeschiedene Aktien fehlen bewusst, siehe Hinweis unten). {active.scoredCount} von{" "}
        {active.universeSize} Aktien hatten zum Startjahr {active.startYear} genug historische Daten
        für einen retroaktiven Score.
      </p>
    </div>
  );
}
